import type { Records, IStorage, StoreName } from "@bandstand/core/IStorage";

const STORES: StoreName[] = ["songs", "chordSheets", "setlists"];

/** Holds files when the browser can't write to its private file system. */
const FILES = "files";

/**
 * Stores the library in the browser: records in IndexedDB, and chord sheets as `.chordpro` files in the
 * origin private file system (OPFS), a private folder the browser gives each site.
 */
export class BrowserStorage implements IStorage {
    private readonly db: Promise<IDBDatabase>;

    private readonly root: Promise<FileSystemDirectoryHandle | undefined>;

    constructor(name = "bandstand") {
        this.db = open(name);
        this.root = privateFolder();
    }

    async readFile(path: string): Promise<string> {
        const place = await this.place(path, false);

        if (!place) {
            const text = await this.run<string | undefined>(FILES, "readonly", (store) => store.get(path));

            if (text === undefined) throw new Error(`No file at ${path}`);

            return text;
        }

        const handle = await place.folder.getFileHandle(place.name);

        return (await handle.getFile()).text();
    }

    async writeFile(path: string, text: string): Promise<void> {
        const place = await this.place(path, true);

        if (!place) {
            await this.run(FILES, "readwrite", (store) => store.put(text, path));

            return;
        }

        const handle = await place.folder.getFileHandle(place.name, { create: true });
        const writable = await handle.createWritable();

        await writable.write(text);
        await writable.close();
    }

    async deleteFile(path: string): Promise<void> {
        const place = await this.place(path, false);

        if (!place) {
            await this.run(FILES, "readwrite", (store) => store.delete(path));

            return;
        }

        await place.folder.removeEntry(place.name).catch(() => undefined);
    }

    async get<S extends StoreName>(store: S, id: string): Promise<Records[S] | undefined> {
        return this.run<Records[S] | undefined>(store, "readonly", (objects) => objects.get(id));
    }

    async getAll<S extends StoreName>(store: S): Promise<Records[S][]> {
        return this.run<Records[S][]>(store, "readonly", (objects) => objects.getAll());
    }

    async put<S extends StoreName>(store: S, record: Records[S]): Promise<void> {
        await this.run(store, "readwrite", (objects) => objects.put(record));
    }

    async delete(store: StoreName, id: string): Promise<void> {
        await this.run(store, "readwrite", (objects) => objects.delete(id));
    }

    /** The folder and file name for a path, or nothing when files go in IndexedDB instead. */
    private async place(path: string, create: boolean): Promise<{ folder: FileSystemDirectoryHandle; name: string } | undefined> {
        let folder = await this.root;

        if (!folder) return undefined;

        const parts = path.split("/");
        const name = parts.pop()!;

        for (const part of parts) folder = await folder.getDirectoryHandle(part, { create });

        return { folder, name };
    }

    private async run<T>(store: string, mode: IDBTransactionMode, request: (objects: IDBObjectStore) => IDBRequest): Promise<T> {
        const transaction = (await this.db).transaction(store, mode);
        const pending = request(transaction.objectStore(store));

        return new Promise((resolve, reject) => {
            transaction.oncomplete = () => resolve(pending.result as T);
            transaction.onerror = () => reject(transaction.error);
            transaction.onabort = () => reject(transaction.error);
        });
    }
}

function open(name: string): Promise<IDBDatabase> {
    const request = indexedDB.open(name, 1);

    request.onupgradeneeded = () => {
        for (const store of STORES) request.result.createObjectStore(store, { keyPath: "id" });

        request.result.createObjectStore(FILES);
    };

    return new Promise((resolve, reject) => {
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
    });
}

/** The private folder, if the browser has one that it can write files to. */
async function privateFolder(): Promise<FileSystemDirectoryHandle | undefined> {
    const canWrite = typeof FileSystemFileHandle !== "undefined" && "createWritable" in FileSystemFileHandle.prototype;

    if (!canWrite || typeof navigator.storage?.getDirectory !== "function") return undefined;

    return navigator.storage.getDirectory().catch(() => undefined);
}
